import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSnapshot } from 'valtio';

import getUserApiKey from '@cartwhl/common/api/getUserApiKey';
import * as noticesStore from '@cartwhl/common/stores/noticesStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import { getHomepageUrl } from '@cartwhl/common/utils/getHomepageUrl';
import {
  getCartwheelApiKeysUpgradeMessaging,
  orgPlanBlocksCartwheelApiKeyManagement,
} from '@cartwhl/common/utils/organizationWorkspace';

import Dropdown from '../atoms/Dropdown';
import Input from '../atoms/Input';
import Text from '../base/TextNew';
import CopyIcon from '../icons/CopyIcon';
import EyeIcon from '../icons/EyeIcon';
import FileIcon from '../icons/FileIcon';
import PlusIcon from '../icons/PlusIcon';
import ThreeDotsIcon from '../icons/ThreeDotsIcon';
import Button from '../input/Button';
import EmptyState from '../messaging/EmptyState';
import Modal from '../molecules/Modal';

export interface ApiKeysPanelProps {
  className?: string;
}

const API_GUIDE_URL = 'https://api-docs.getcartwheel.com';
const apiKeyDateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export default function ApiKeysPanel({ className }: ApiKeysPanelProps) {
  const {
    organizationInfo,
    organizationServiceAccounts,
    loading: userLoading,
    userInfo,
  } = useSnapshot(userStore.state);

  const [serviceName, setServiceName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [keyValues, setKeyValues] = useState<Record<string, string>>({});
  const [keyLoading, setKeyLoading] = useState<Record<string, boolean>>({});
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    const orgId = userInfo.organizationID;
    if (!orgId) return;
    void userStore.fetchOrganization(orgId);
  }, [userInfo.organizationID]);

  const orgDetailPending =
    Boolean(userInfo.organizationID) && organizationInfo == null;

  const canManageApiKeys = useMemo(
    () =>
      Boolean(userInfo.organizationID) &&
      Boolean(organizationInfo) &&
      !orgPlanBlocksCartwheelApiKeyManagement(organizationInfo?.planType),
    [userInfo.organizationID, organizationInfo],
  );

  const upgradeMessaging = useMemo(
    () => getCartwheelApiKeysUpgradeMessaging(organizationInfo?.planType),
    [organizationInfo?.planType],
  );

  const hasEditAccess = organizationInfo?.accessLevel === 'EDIT';

  const atApiKeyLimit = useMemo(() => {
    if (!organizationInfo) return false;
    const active = organizationInfo.activeAPIKeys ?? 0;
    const allowed = organizationInfo.allowedNumAPIKeys;
    if (allowed === undefined) return false;
    return active >= allowed;
  }, [organizationInfo]);

  const handleCreateKey = useCallback(async () => {
    const orgId = userInfo.organizationID;
    if (!orgId) return;
    setCreating(true);
    const name = serviceName.trim() || 'API Key';
    const result = await userStore.createNewServiceAccount(name);
    setCreating(false);
    if (result) {
      setServiceName('');
      noticesStore.info('API key created');
    }
  }, [serviceName, userInfo.organizationID]);

  const toggleReveal = useCallback(
    async (serviceID: string) => {
      if (revealed[serviceID]) {
        setRevealed((prev) => ({ ...prev, [serviceID]: false }));
        return;
      }
      setKeyLoading((prev) => ({ ...prev, [serviceID]: true }));
      const { data, error } = await getUserApiKey(serviceID);
      setKeyLoading((prev) => ({ ...prev, [serviceID]: false }));
      if (error) {
        noticesStore.error(error.message);
        return;
      }
      if (data?.apiKey) {
        setKeyValues((prev) => ({ ...prev, [serviceID]: data.apiKey }));
        setRevealed((prev) => ({ ...prev, [serviceID]: true }));
      }
    },
    [revealed],
  );

  const handleCopyKey = useCallback(
    async (serviceID: string) => {
      let fullKeyToCopy: string | undefined = keyValues[serviceID];
      if (!fullKeyToCopy) {
        const { data, error } = await getUserApiKey(serviceID);
        if (error) {
          noticesStore.error(error.message);
          return;
        }
        fullKeyToCopy = data?.apiKey;
        if (!fullKeyToCopy) {
          noticesStore.error('Failed to get API key');
          return;
        }
        setKeyValues((prev) => ({ ...prev, [serviceID]: fullKeyToCopy! }));
      }

      await navigator.clipboard.writeText(fullKeyToCopy);
      noticesStore.info('API key copied');
    },
    [keyValues],
  );

  const accounts = organizationServiceAccounts ?? [];
  const planMaximumText = organizationInfo
    ? `Plan maximum of ${organizationInfo.allowedNumAPIKeys ?? '—'} keys`
    : undefined;

  const deleteTargetAccount = useMemo(() => {
    const list = organizationServiceAccounts ?? [];
    return list.find((account) => account.serviceID === deleteTargetId) ?? null;
  }, [organizationServiceAccounts, deleteTargetId]);

  const handleConfirmDeleteKey = useCallback(async () => {
    if (!deleteTargetId) return;
    setDeleteSubmitting(true);
    const serviceID = deleteTargetId;
    const ok = await userStore.deleteOrganizationServiceAccount(serviceID);
    setDeleteSubmitting(false);
    if (ok) {
      noticesStore.info('API key deleted');
      setDeleteTargetId(null);
      setRevealed((prev) => {
        const next = { ...prev };
        delete next[serviceID];
        return next;
      });
      setKeyValues((prev) => {
        const next = { ...prev };
        delete next[serviceID];
        return next;
      });
    }
  }, [deleteTargetId]);

  if (userLoading || orgDetailPending) {
    return (
      <ApiKeysPanelLoadingState maxWidthClassName="max-w-(--breakpoint-lg)" />
    );
  }

  return (
    <div className={className}>
      {!canManageApiKeys ? (
        <div className="mt-8">
          <ApiKeysPanelSection
            title="API keys"
            actions={<ApiGuideLink />}
            contentClassName="flex min-h-[280px] flex-col items-center justify-center gap-6 px-6 py-12 text-center"
          >
            <Text as="p" token="14" className="text-primary max-w-md">
              Upgrade to {upgradeMessaging.planLabel} to start using API keys
            </Text>
            <a
              href={getHomepageUrl(upgradeMessaging.pricingPath)}
              className="inline-flex no-underline"
            >
              <Button variant="primary" size="lg">
                Upgrade to {upgradeMessaging.planLabel}
              </Button>
            </a>
          </ApiKeysPanelSection>
        </div>
      ) : (
        <div className="mt-8 space-y-14">
          <ApiKeysPanelSection
            title="Create new key"
            actions={<ApiGuideLink />}
            className="gap-6"
          >
            {hasEditAccess ? (
              <>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <Input
                    name="serviceName"
                    placeholder="Name for new key"
                    value={serviceName}
                    onChange={(event) => setServiceName(event.target.value)}
                    className="w-full flex-1"
                    aria-label="Name for new API key"
                  />
                  <Button
                    disabled={creating || atApiKeyLimit}
                    icon={<PlusIcon />}
                    variant="secondary"
                    size="sm"
                    className="self-end sm:self-auto"
                    onClick={handleCreateKey}
                  >
                    Create new key
                  </Button>
                </div>
                {atApiKeyLimit && organizationInfo && (
                  <Text as="p" token="14" className="text-tertiary mt-4">
                    You are using all allowed API keys (
                    {organizationInfo.activeAPIKeys ?? 0} of{' '}
                    {organizationInfo.allowedNumAPIKeys ?? 0}). Delete a key
                    below to create another.
                  </Text>
                )}
              </>
            ) : (
              <Text as="p" token="14" className="text-tertiary">
                API key management is only available to workspace admins.
              </Text>
            )}
          </ApiKeysPanelSection>

          <ApiKeysPanelSection
            title="API keys"
            boxed={false}
            className="gap-6"
            actions={
              planMaximumText ? (
                <Text as="p" token="14" className="text-tertiary text-right">
                  {planMaximumText}
                </Text>
              ) : undefined
            }
          >
            {accounts.length === 0 ? (
              <div className="border-primary bg-primary rounded-xl border p-6">
                <EmptyState
                  description={`No API keys yet.${
                    hasEditAccess
                      ? ' Use the "Create new key" section above.'
                      : ' Ask an account admin to create a key for your account.'
                  }`}
                />
              </div>
            ) : (
              <div className="space-y-4">
                {accounts.map((account) => {
                  const isRevealed = revealed[account.serviceID];
                  const fullKey = keyValues[account.serviceID];
                  const loading = keyLoading[account.serviceID];
                  const accountName = account.serviceName || 'Unnamed key';
                  return (
                    <div
                      key={account.serviceID}
                      className="border-primary bg-primary relative rounded-xl border px-6 py-6"
                    >
                      <div className="absolute right-6 top-6 sm:hidden">
                        <ApiKeyActionsMenu
                          serviceName={accountName}
                          onCopy={() => handleCopyKey(account.serviceID)}
                          onDelete={() => setDeleteTargetId(account.serviceID)}
                        />
                      </div>

                      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start gap-2">
                            <ApiKeyGlyph className="text-primary h-5 w-5 shrink-0" />
                            <div className="min-w-0 flex-1 pr-12 sm:pr-0">
                              <Text
                                as="p"
                                token="14/medium"
                                className="text-primary truncate"
                              >
                                {accountName}
                              </Text>
                              <Text
                                as="p"
                                token="14"
                                className={`mt-2 break-all ${
                                  isRevealed && fullKey
                                    ? 'text-primary font-mono'
                                    : 'text-tertiary'
                                }`}
                              >
                                {isRevealed && fullKey
                                  ? fullKey
                                  : `••••••••••••${account.apiKeyLast4Digits || '????'}`}
                              </Text>

                              <div className="mt-4 sm:hidden">
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  loading={loading}
                                  icon={<EyeIcon />}
                                  onClick={() =>
                                    toggleReveal(account.serviceID)
                                  }
                                >
                                  {isRevealed ? 'Hide' : 'Reveal key'}
                                </Button>
                              </div>

                              <div className="mt-6">
                                <Text token="14" className="text-tertiary">
                                  Created {formatApiKeyDate(account.createdAt)}
                                </Text>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="hidden shrink-0 items-center gap-2 sm:flex">
                          <Button
                            variant="secondary"
                            size="sm"
                            loading={loading}
                            icon={<EyeIcon />}
                            onClick={() => toggleReveal(account.serviceID)}
                          >
                            {isRevealed ? 'Hide' : 'Reveal key'}
                          </Button>
                          <ApiKeyActionsMenu
                            serviceName={accountName}
                            onCopy={() => handleCopyKey(account.serviceID)}
                            onDelete={() =>
                              setDeleteTargetId(account.serviceID)
                            }
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ApiKeysPanelSection>

          {organizationInfo && (
            <ApiKeysPanelSection title="API key details" className="gap-6">
              <dl className="divide-primary divide-y">
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-5 first:pt-0">
                  <Text as="dt" token="14" className="text-secondary">
                    Maximum # of keys
                  </Text>
                  <Text
                    as="dd"
                    token="14"
                    className="text-tertiary text-right tabular-nums"
                  >
                    {organizationInfo.allowedNumAPIKeys ?? '—'}
                  </Text>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-5">
                  <Text as="dt" token="14" className="text-secondary">
                    Maximum batch size
                  </Text>
                  <Text
                    as="dd"
                    token="14"
                    className="text-tertiary text-right tabular-nums"
                  >
                    {organizationInfo.allowedNumBatchSize ?? '—'}
                  </Text>
                </div>
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-5 last:pb-0">
                  <Text as="dt" token="14" className="text-secondary">
                    Concurrent batches allowed
                  </Text>
                  <Text
                    as="dd"
                    token="14"
                    className="text-tertiary text-right tabular-nums"
                  >
                    {organizationInfo.allowedNumBatches ?? '—'}
                  </Text>
                </div>
              </dl>
            </ApiKeysPanelSection>
          )}
        </div>
      )}

      {deleteTargetId && (
        <Modal
          title="Delete API key?"
          open
          maxWidth={440}
          onOpenChange={(open) => {
            if (!open && !deleteSubmitting) setDeleteTargetId(null);
          }}
        >
          <div className="flex flex-col gap-4">
            <Text as="p" token="14" className="text-primary">
              This cannot be undone and will revoke the key immediately. Any
              integrations using it will stop working.
            </Text>
            {deleteTargetAccount && (
              <div className="space-y-1">
                <Text as="p" token="14/medium" className="text-primary">
                  {deleteTargetAccount.serviceName || 'Unnamed key'}
                </Text>
                <Text token="14" className="text-tertiary font-mono">
                  Service ID: {deleteTargetAccount.serviceID}
                </Text>
              </div>
            )}
            <div className="mt-2 flex flex-row flex-nowrap">
              <Button
                variant="secondary"
                onClick={() => setDeleteTargetId(null)}
                disabled={deleteSubmitting}
              >
                Cancel
              </Button>
              <Button
                className="ml-auto"
                variant="primary-destructive"
                onClick={handleConfirmDeleteKey}
                disabled={deleteSubmitting}
                loading={deleteSubmitting}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

interface ApiKeysPanelSectionProps {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
  boxed?: boolean;
  className?: string;
  contentClassName?: string;
}

function ApiKeysPanelSection({
  title,
  actions,
  children,
  boxed = true,
  className,
  contentClassName,
}: ApiKeysPanelSectionProps) {
  return (
    <section className={`flex flex-col gap-5 ${className ?? ''}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Text as="h2" token="14/medium">
          {title}
        </Text>
        {actions}
      </div>

      {boxed ? (
        <div
          className={`border-primary bg-primary rounded-xl border p-6 shadow-sm ${
            contentClassName ?? ''
          }`}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </section>
  );
}

interface ApiKeysPanelLoadingStateProps {
  className?: string;
  maxWidthClassName?: string;
}

function ApiKeysPanelLoadingState({
  className,
  maxWidthClassName = 'max-w-(--breakpoint-md)',
}: ApiKeysPanelLoadingStateProps) {
  return (
    <div className={`mx-auto ${maxWidthClassName} ${className ?? ''}`}>
      <div className="mt-8 flex flex-col gap-4">
        <div className="bg-tertiary h-5 w-24 animate-pulse rounded" />
        <div className="border-primary flex h-[210px] items-center justify-center rounded-lg border p-6">
          <div className="bg-tertiary h-8 w-8 animate-pulse rounded-full" />
        </div>
      </div>
    </div>
  );
}

function ApiGuideLink() {
  return (
    <Text
      as="a"
      token="14/medium"
      href={API_GUIDE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue flex items-center gap-1.5 hover:underline"
    >
      <FileIcon className="text-blue shrink-0" />
      API guide
    </Text>
  );
}

interface ApiKeyActionsMenuProps {
  serviceName: string;
  onCopy: () => void;
  onDelete: () => void;
}

function ApiKeyActionsMenu({
  serviceName,
  onCopy,
  onDelete,
}: ApiKeyActionsMenuProps) {
  return (
    <Dropdown
      trigger={
        <Button
          variant="secondary"
          size="sm"
          icon={<ThreeDotsIcon />}
          aria-label={`More actions for ${serviceName}`}
        />
      }
    >
      <Dropdown.Item icon={<CopyIcon />} onClick={onCopy}>
        Copy
      </Dropdown.Item>
      <Dropdown.Item onClick={onDelete}>
        <span className="text-red">Delete</span>
      </Dropdown.Item>
    </Dropdown>
  );
}

function ApiKeyGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 21 21"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M18.0534 2.2004C16.8621 1.00744 15.3036 0.250655 13.6294 0.0521698C11.9552 -0.146316 10.263 0.225074 8.82576 1.10641C7.38854 1.98775 6.29018 3.32762 5.70793 4.90981C5.12568 6.49201 5.09349 8.22423 5.61657 9.82696L0.439695 15.0038C0.299801 15.1426 0.188889 15.3078 0.113407 15.4899C0.0379247 15.6719 -0.000621974 15.8671 7.58898e-06 16.0641V18.7538C7.58898e-06 19.1517 0.158043 19.5332 0.439347 19.8145C0.720652 20.0958 1.10218 20.2538 1.50001 20.2538H4.50001C4.69892 20.2538 4.88969 20.1748 5.03034 20.0342C5.17099 19.8935 5.25001 19.7028 5.25001 19.5038V18.0038H6.75001C6.94892 18.0038 7.13969 17.9248 7.28034 17.7842C7.42099 17.6435 7.50001 17.4528 7.50001 17.2538V15.7538H9.00001C9.09853 15.7539 9.1961 15.7346 9.28715 15.6969C9.37819 15.6593 9.46093 15.6041 9.53063 15.5345L10.4269 14.6373C11.1771 14.8813 11.9611 15.005 12.75 15.0038H12.7594C14.242 15.002 15.6908 14.5609 16.9228 13.736C18.1548 12.9112 19.1146 11.7398 19.6812 10.3697C20.2477 8.99958 20.3954 7.49231 20.1057 6.03829C19.816 4.58427 19.1018 3.24873 18.0534 2.2004ZM18.75 7.70071C18.6478 10.8967 15.9609 13.5001 12.7603 13.5038H12.75C11.9908 13.5051 11.2383 13.3616 10.5328 13.081C10.3948 13.0211 10.242 13.0041 10.0942 13.0322C9.94641 13.0603 9.81048 13.1322 9.70407 13.2385L8.6897 14.2538H6.75001C6.5511 14.2538 6.36033 14.3329 6.21968 14.4735C6.07903 14.6142 6.00001 14.8049 6.00001 15.0038V16.5038H4.50001C4.3011 16.5038 4.11033 16.5829 3.96968 16.7235C3.82903 16.8642 3.75001 17.0549 3.75001 17.2538V18.7538H1.50001V16.0641L7.01532 10.5498C7.12166 10.4434 7.19355 10.3074 7.22163 10.1596C7.24972 10.0119 7.23271 9.85903 7.17282 9.72103C6.89133 9.0132 6.74782 8.25807 6.75001 7.49634C6.75001 4.29571 9.35719 1.60884 12.5531 1.50665C13.3741 1.47925 14.1919 1.62071 14.9559 1.92226C15.72 2.22381 16.414 2.67902 16.9949 3.25971C17.5759 3.84039 18.0314 4.53418 18.3333 5.29809C18.6352 6.062 18.777 6.87976 18.75 7.70071ZM15.75 5.62884C15.75 5.85134 15.684 6.06885 15.5604 6.25385C15.4368 6.43886 15.2611 6.58305 15.0555 6.66819C14.85 6.75336 14.6238 6.77567 14.4056 6.73227C14.1874 6.68888 13.987 6.58175 13.8297 6.42443C13.6724 6.2671 13.5652 6.06667 13.5218 5.84848C13.4784 5.63029 13.5005 5.4041 13.5856 5.19854C13.6708 4.99299 13.8149 4.81726 13.9999 4.69366C14.1849 4.57005 14.4025 4.50397 14.625 4.50384C14.9233 4.50384 15.2095 4.62237 15.4204 4.83336C15.6314 5.04433 15.75 5.33046 15.75 5.62884Z" />
    </svg>
  );
}

function formatApiKeyDate(date: string) {
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return '—';
  return apiKeyDateFormatter.format(parsedDate);
}
